import { useEffect, useState } from "react";
import { Menu as MenuIcon, X } from "lucide-react";
import { UBER_EATS_URL, LOGO_URL } from "@/config/site";
import { Link } from "react-router-dom";

const NAV_LINKS = [
  { label: "Menu", href: "#menu" },
  { label: "Location", href: "#location" },
  { label: "About", href: "#about" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-[#0A0A0A]/95 backdrop-blur-md border-b border-white/10" : "bg-gradient-to-b from-black/80 to-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="header-logo-link">
            <img
              src={LOGO_URL}
              alt="Smashed Burger & Bao Boy"
              className="h-12 sm:h-14 w-auto"
              loading="eager"
            />
            <span className="hidden md:block font-heading text-xl tracking-wider text-white leading-none">
              SMASHED BURGER<br /><span className="text-brand-orange">& BAO BOY</span>
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-8">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                className="font-subheading font-semibold text-sm tracking-widest uppercase text-white/80 hover:text-brand-orange transition-colors"
                data-testid={`nav-link-${l.label.toLowerCase()}`}
              >
                {l.label}
              </a>
            ))}
            <a
              href={UBER_EATS_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-order-cta"
              className="bg-brand-orange hover:bg-[#FF7A1A] text-white font-subheading font-bold uppercase tracking-widest text-sm px-6 py-3 rounded-full transition-all shadow-[0_4px_20px_0_rgba(255,102,0,0.45)] hover:shadow-[0_6px_25px_0_rgba(255,102,0,0.7)] hover:-translate-y-0.5"
            >
              Order Delivery
            </a>
          </nav>

          {/* Mobile actions */}
          <div className="flex items-center gap-2 md:hidden">
            <a
              href={UBER_EATS_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-order-cta-mobile"
              className="bg-brand-orange text-white font-subheading font-bold uppercase tracking-wider text-xs px-4 py-2.5 rounded-full shadow-[0_4px_15px_0_rgba(255,102,0,0.4)]"
            >
              Order
            </a>
            <button
              onClick={() => setOpen((v) => !v)}
              aria-label="Toggle menu"
              data-testid="mobile-menu-toggle"
              className="p-2 text-white"
            >
              {open ? <X size={24} /> : <MenuIcon size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu drawer */}
      {open && (
        <div
          data-testid="mobile-menu-drawer"
          className="md:hidden bg-[#0A0A0A] border-t border-white/10"
        >
          <div className="px-4 py-4 flex flex-col gap-4">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                className="font-subheading font-semibold uppercase tracking-widest text-white/90 hover:text-brand-orange py-2 border-b border-white/5"
              >
                {l.label}
              </a>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
